/**
 * DashboardConfigViewPage
 * Loads a Metabase iframe for a specific dashboard config item.
 * Navigated to from DashboardConfigPage when a card is clicked.
 * Has a back button to return to the list.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2, Star, RefreshCw, ExternalLink } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { enrollerAdminService } from '@/services/ea/enrollerAdminService';
import { useDashboardConfigs } from '@/hooks/dashboardConfig/useDashboardConfig';
import type { NormalisedDashboardItem } from '@/types/dashboardConfig';

const REFRESH_BUFFER_MS = 60_000;

const DashboardConfigViewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const loginUserId = user?.id ?? null;

  const { items, isLoading: itemsLoading } = useDashboardConfigs(loginUserId, 'ED');
  const [item, setItem] = useState<NormalisedDashboardItem | null>(null);

  // Resolve item once data is loaded
  useEffect(() => {
    if (!itemsLoading && items.length > 0 && id) {
      const found = items.find((i) => String(i.id) === id);
      setItem(found ?? null);
    }
  }, [items, itemsLoading, id]);

  // Set page title
  useEffect(() => {
    document.title = item ? `${item.customName} | Dashboard` : 'Dashboard | Admin';
  }, [item]);

  // ── Iframe ──────────────────────────────────────────────────────────────────
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const loadIframe = useCallback(
    async (dashItem: NormalisedDashboardItem) => {
      if (!user || !token) return;
      clearRefreshTimer();
      setIframeUrl(null);
      setIframeError(null);
      setIframeLoading(true);
      enrollerAdminService.updateContext({ userId: user.id, token });
      try {
        const response = await enrollerAdminService.getMetabaseToken({
          type: dashItem.resourceType ?? 'dashboard',
          id: dashItem.resourceId,
          user_email: user.email ?? '',
          filters: dashItem.dashboardFilters ?? [],
          userContext: {
            userId: user.id,
            userEmail: user.email,
            userFullName: user.fullName ?? '',
          },
        });
        if (response?.token) {
          const url = enrollerAdminService.buildMetabaseEmbedUrl(
            dashItem.resourceType ?? 'dashboard',
            response.token
          );
          setIframeUrl(url);
          if (response.expires_at_epoch_ms) {
            const delay = response.expires_at_epoch_ms - Date.now() - REFRESH_BUFFER_MS;
            if (delay > 0) {
              refreshTimerRef.current = setTimeout(() => loadIframe(dashItem), delay);
            }
          }
        } else {
          setIframeError('No token returned from server');
        }
      } catch (err) {
        setIframeError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        setIframeLoading(false);
      }
    },
    [user, token, clearRefreshTimer]
  );

  useEffect(() => {
    if (item) loadIframe(item);
  }, [item]);

  useEffect(() => () => clearRefreshTimer(), [clearRefreshTimer]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground hover:text-foreground -ml-1"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>

        {item && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-foreground truncate">{item.customName}</h1>
            {item.metabaseQuestionName && item.metabaseQuestionName !== item.customName && (
              <span className="text-sm text-muted-foreground truncate">
                ({item.metabaseQuestionName})
              </span>
            )}
            {item.isDefault && (
              <Badge className="shrink-0 gap-1 text-[10px] py-0 px-1.5 bg-warning/20 text-warning-foreground border-warning/30">
                <Star className="h-2.5 w-2.5 fill-current" />
                Default
              </Badge>
            )}
            {item.userTypes.map((role) => (
              <Badge key={role} variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">
                {role}
              </Badge>
            ))}
          </div>
        )}

        {item && iframeUrl && !iframeLoading && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 shrink-0"
            onClick={() => loadIframe(item)}
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
        )}

        {item && iframeUrl && !iframeLoading && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 shrink-0"
            onClick={() => window.open('https://analytics.hbox.ai/auth/login', '_blank', 'noopener,noreferrer')}
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in Metabase
          </Button>
        )}
      </div>

      {/* Loading item */}
      {itemsLoading && (
        <div className="flex items-center gap-2 text-muted-foreground text-sm py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      )}

      {/* Item not found */}
      {!itemsLoading && !item && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            Dashboard config not found.
          </CardContent>
        </Card>
      )}

      {/* Iframe loading */}
      {item && iframeLoading && (
        <div className="flex items-center justify-center py-24 text-muted-foreground border border-border rounded-lg">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
          Loading dashboard…
        </div>
      )}

      {/* Iframe error */}
      {item && iframeError && !iframeLoading && (
        <Card>
          <CardContent className="p-6 text-center text-destructive text-sm space-y-3">
            <p>{iframeError}</p>
            <Button size="sm" variant="outline" onClick={() => loadIframe(item)}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Iframe */}
      {item && iframeUrl && !iframeLoading && (
        <Card>
          <CardContent className="p-0 rounded-lg overflow-auto">
            <iframe
              src={iframeUrl}
              width="100%"
              className="block w-full h-[calc(100vh-160px)] max-h-[calc(100vh-160px)] min-h-[600px]"
              title={item.customName}
              allowFullScreen
              scrolling="auto"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardConfigViewPage;
